import { useEffect, useState } from 'react';
import { toTitleCase, urlformatter } from '../Utils/CommonUtils';
import { ifscDetailsAPIInterface } from './types';
import getConfig from 'next/config';

interface IndividualsIfscProps {
  bankDetails: ifscDetailsAPIInterface;
}
const IndividualsIfsc = ({
  bankDetails,
}: IndividualsIfscProps): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();

  const [copyText, setCopyText] = useState('Copy');
  useEffect(() => {
    const timer = setTimeout(() => {
      setCopyText('Copy');
    }, 7000);
    return () => clearTimeout(timer);
  }, [copyText]);

  if (!bankDetails) {
    return <div></div>;
  }
  const {
    bank_name,
    branch_name,
    ifsc_code,
    micr_code,
    address,
    contact_number,
    city,
    district,
    state,
    new_ifsc_code,
  } = bankDetails;

  const getBankDetailStruct = [
    {
      id: 'bank_name',
      label: 'Bank',
      value: bank_name.toUpperCase(),
    },
    {
      id: 'ifsc',
      label: 'IFSC',
      value: ifsc_code,
    },
    {
      id: 'micr',
      label: 'MICR Code',
      value: micr_code || 'N/A',
    },
    {
      id: 'branch-code',
      label: 'Branch Code',
      value: `${ifsc_code.slice(-6)} (Last Six Characters of IFSC Code)`,
    },
    {
      id: 'branch',
      label: 'Branch',
      value: toTitleCase(branch_name) || 'N/A',
    },
    {
      id: 'city',
      label: 'City',
      value: toTitleCase(city) || 'N/A',
    },
    {
      id: 'district',
      label: 'District',
      value: toTitleCase(district) || 'N/A',
    },
    {
      id: 'state',
      label: 'State',
      value: toTitleCase(state) || 'N/A',
    },
    {
      id: 'add',
      label: 'Address',
      value: toTitleCase(address) || 'N/A',
    },
    {
      id: 'phone',
      label: 'Phone No',
      value: contact_number || 'N/A',
    },
  ];

  const copyBtnClick = (ifsc: string) => {
    navigator.clipboard.writeText(ifsc);
    setCopyText('Copied');
  };

  return (
    <>
      <h1 className="text-s-48 sm:text-s-24 sm:mt-6">
        IFSC Code of {toTitleCase(bank_name)}, {toTitleCase(branch_name)},{' '}
        {toTitleCase(state)}
      </h1>
      <p className="py-6">
        Here is the IFSC & MICR Codes of {toTitleCase(bank_name)},
        {toTitleCase(branch_name)}, {toTitleCase(city)}, {toTitleCase(state)}.
        Also get the latest address, contact number of {toTitleCase(bank_name)},{' '}
        {toTitleCase(branch_name)}.
      </p>
      <h2 className="text-s-40 mt-6 sm:hidden">IFSC code</h2>

      {new_ifsc_code ? (
        <div className="flex flex-wrap mt-8">
          <div className="flex items-center w-1/2 sm:w-full">
            <h2 className="text-s-32 sm:text-base font-semibold">
              {ifsc_code}
            </h2>
            <span className="ml-4 old-btn">Old</span>
          </div>
          <div className="flex items-center w-1/2 sm:w-full sm:mt-2">
            <h2 className="text-s-32 sm:text-base font-semibold">
              {new_ifsc_code}
            </h2>
            <span className="ml-4 new-btn">New</span>
            <span
              data-testd="copy-btn"
              className="text-blue-350 text-s-24 sm:text-base font-bold ml-6 sm:ml cursor-pointer"
              onClick={() => copyBtnClick(new_ifsc_code)}
            >
              {' '}
              {copyText}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap mt-8">
          <div className="flex items-center w-full sm:w-full sm:mt-2">
            <h2 className="text-s-32 sm:text-base font-semibold">
              {ifsc_code}
            </h2>
            <span
              data-testid="copy-btn"
              className="text-blue-350 text-s-24 sm:text-base font-bold ml-6 sm:ml cursor-pointer"
              onClick={() => copyBtnClick(ifsc_code)}
            >
              {' '}
              {copyText}
            </span>
          </div>
        </div>
      )}

      <div className="py-6 text-s-18 sm:text-base">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white rounded-lg shadow-sm border border-gray-200">
            <tbody>
              {getBankDetailStruct.map((item, index) => (
                <tr
                  key={item.id}
                  className={`border-b border-gray-300 hover:bg-gray-50 transition-colors ${
                    index === getBankDetailStruct.length - 1 ? 'border-b-0' : ''
                  }`}
                >
                  <td
                    className="py-4 px-4 sm:px-6 text-gray-600 font-medium min-w-0 w-1/3 sm:w-auto"
                    valign="top"
                  >
                    <div className="break-words">{item.label}:</div>
                  </td>
                  <td
                    className="py-4 px-4 sm:px-6 text-gray-900 min-w-0 flex-1"
                    valign="top"
                  >
                    <div className="font-semibold break-all">
                      {item.id == 'bank_name' ? (
                        <a
                          className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          href={`${
                            publicRuntimeConfig.CLEARTAX_DOMAIN
                          }/ifsc-code/${urlformatter(item.value)}/`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <>{item.value}</>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default IndividualsIfsc;
